import type { PaperWidth } from "./types";

export type PrintDirection = "rtl" | "ltr";

export interface PrintDocumentLine {
  text: string;
  detail?: string;
  emphasis?: "normal" | "strong" | "marker";
  align?: "start" | "center" | "end";
  indent?: boolean;
  direction?: PrintDirection;
}

export interface PrintDocumentInput {
  title: string;
  language: "ar" | "en";
  direction?: PrintDirection;
  paperWidth: PaperWidth;
  lines: readonly PrintDocumentLine[];
}

const PAPER_WIDTH_MM: Record<PaperWidth, number> = {
  "58mm": 58,
  "80mm": 80,
};

export function paperWidthMm(width: PaperWidth): number {
  return PAPER_WIDTH_MM[width];
}

export function escapePrintHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}

export function buildPrintDocument(input: PrintDocumentInput): string {
  const width = paperWidthMm(input.paperWidth);
  const direction = input.direction ?? (input.language === "ar" ? "rtl" : "ltr");
  const title = escapePrintHtml(input.title);
  const lines = input.lines
    .map((line) => {
      const lineDirection = line.direction ?? direction;
      const emphasis = line.emphasis ?? "normal";
      const align = line.align ?? "start";
      const classes = ["line", emphasis, line.indent ? "indent" : ""]
        .filter(Boolean)
        .join(" ");
      const detail = line.detail
        ? `<small>${escapePrintHtml(line.detail)}</small>`
        : "";
      return `<div class="${classes}" dir="${lineDirection}" style="text-align:${align}"><span>${escapePrintHtml(line.text)}</span>${detail}</div>`;
    })
    .join("");

  return `<!doctype html><html lang="${input.language}" dir="${direction}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${title}</title><style>@page{size:${width}mm auto;margin:3mm}*{box-sizing:border-box}html,body{margin:0;padding:0;background:#fff;color:#000}body{width:${width - 6}mm;font-family:Arial,"Noto Sans Arabic",sans-serif;font-size:12px;line-height:1.35;unicode-bidi:plaintext}.line{display:flex;justify-content:space-between;gap:2mm;margin:0 0 1.5mm;overflow-wrap:anywhere}.line.indent{padding-inline-start:3mm}.line.strong{font-size:14px;font-weight:800}.line.marker{border:2px solid #000;padding:1.5mm;font-size:15px;font-weight:800;text-align:center!important}.line small{display:block;font-size:10px;font-weight:400}</style></head><body><main aria-label="${title}">${lines}</main></body></html>`;
}
